import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import API from '../api/axios';
import { formatRole } from '../utils/roleLabel';
import './CollegeAdminDashboard.css';

const BuildingIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="18" height="18" rx="2" /><path d="M3 9h18M9 21V9" />
  </svg>
);
const BookIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 19.5A2.5 2.5 0 016.5 17H20" /><path d="M6.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15A2.5 2.5 0 016.5 2z" />
  </svg>
);
const FileIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" /><polyline points="14 2 14 8 20 8" />
  </svg>
);
const UsersIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 00-3-3.87" /><path d="M16 3.13a4 4 0 010 7.75" />
  </svg>
);

const quickActions = [
  { path: '/college-admin/departments', icon: <BuildingIcon />, color: 'blue', label: 'Departments', desc: 'Manage college departments' },
  { path: '/college-admin/subjects', icon: <BookIcon />, color: 'green', label: 'Subjects', desc: 'Create and manage subjects' },
  { path: '/college-admin/templates?tab=create', icon: <FileIcon />, color: 'purple', label: 'Create Template', desc: 'Build paper templates' },
  { path: '/college-admin/templates?tab=extract', icon: <FileIcon />, color: 'amber', label: 'Extract Template', desc: 'Extract from PDF' },
];

const CollegeAdminDashboard = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    departments: 0,
    subjects: 0,
    templates: 0,
    users: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [deptRes, subjRes, tmplRes, usersRes] = await Promise.all([
          API.get(`/public/departments?college_id=${user.college_id}`).catch(() => ({ data: { departments: [] } })),
          API.get('/subjects').catch(() => ({ data: { subjects: [] } })),
          API.get('/templates').catch(() => ({ data: { templates: [] } })),
          API.get('/users?limit=1').catch(() => ({ data: { total: 0 } })),
        ]);

        const depts = deptRes.data.departments || [];
        const deptIds = new Set(depts.map(d => String(d.department_id)));
        const allSubjects = subjRes.data.subjects || [];
        const collegeSubjects = allSubjects.filter(s => deptIds.has(String(s.department_id)));

        setStats({
          departments: depts.length,
          subjects: collegeSubjects.length,
          templates: (tmplRes.data.templates || []).length,
          users: usersRes.data.total || 0,
        });
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, [user.college_id]);

  if (loading) {
    return (
      <div className="ca-page">
        <div className="ca-spinner-wrap" style={{ minHeight: '400px' }}>
          <div className="ca-spinner" />
        </div>
      </div>
    );
  }

  const statCards = [
    { icon: <BuildingIcon />, colorClass: 'ca-stat-icon--blue', value: stats.departments, label: 'Departments', path: '/college-admin/departments' },
    { icon: <BookIcon />, colorClass: 'ca-stat-icon--green', value: stats.subjects, label: 'Subjects', path: '/college-admin/subjects' },
    { icon: <FileIcon />, colorClass: 'ca-stat-icon--purple', value: stats.templates, label: 'Templates', path: '/college-admin/templates' },
    { icon: <UsersIcon />, colorClass: 'ca-stat-icon--red', value: stats.users, label: 'Users', path: '/college-admin/users' },
  ];

  return (
    <div className="ca-page">
      <div className="ca-page-header">
        <h1>College Administration</h1>
        <p>Welcome back, {user.name} ({formatRole(user.role)})</p>
      </div>

      <div className="ca-stats-grid">
        {statCards.map(card => (
          <div
            key={card.label}
            className="ca-stat-card"
            style={{ cursor: 'pointer' }}
            onClick={() => navigate(card.path)}
          >
            <div className={`ca-stat-icon ${card.colorClass}`}>
              {card.icon}
            </div>
            <div>
              <div className="ca-stat-value">{card.value}</div>
              <div className="ca-stat-label">{card.label}</div>
            </div>
          </div>
        ))}
      </div>

      <div className="ca-quick-actions" style={{ marginTop: '2rem' }}>
        <h2 style={{ fontSize: '1.1rem', fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 1rem' }}>
          Quick Actions
        </h2>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
          gap: '1rem'
        }}>
          {quickActions.map(action => (
            <div
              key={action.path}
              className="ca-stat-card"
              style={{ cursor: 'pointer', padding: '1.25rem' }}
              onClick={() => navigate(action.path)}
            >
              <div className={`ca-stat-icon ${action.color === 'blue' ? 'ca-stat-icon--blue' : action.color === 'green' ? 'ca-stat-icon--green' : action.color === 'purple' ? 'ca-stat-icon--purple' : action.color === 'amber' ? 'ca-stat-icon--amber' : 'ca-stat-icon--red'}`}
                style={{ width: '40px', height: '40px', borderRadius: '8px' }}>
                {action.icon}
              </div>
              <div style={{ marginTop: '0.5rem' }}>
                <div style={{ fontWeight: 600, fontSize: '0.9rem', color: 'var(--text-primary)' }}>
                  {action.label}
                </div>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '0.15rem' }}>
                  {action.desc}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CollegeAdminDashboard;
